import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {UnlinkLine} from '@dropbox/dig-icons/assets';
import {snackbarAtom} from 'atoms/snackbar';
import type {Workstream} from 'client';
import {useSetAtom} from 'jotai';
import {createContext, useContext} from 'react';
import {
  buildSubworkstreamsEdit,
  getSubworkstreamIds,
} from 'views/workstreams/detail/subworkstreamEdit';
import {useWorkstreamEdit} from 'views/workstreams/hooks';

import {invalidateNavTrees} from './useNavTree';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

type SubworkstreamRemoval = {parentName: string; remove: (id: number, name: string) => void};

/**
 * /workstreams: set by the detail page around its own sub-workstream
 * cards, so their hover can unlink them from it. Nested lists (a sub card's own
 * sub-workstreams) are deeper, and only depth-0 cards read it.
 */
export const SubworkstreamRemovalContext = createContext<SubworkstreamRemoval | null>(null);

/**
 * Edits to `parent`'s sub-workstreams from its detail page, each saved at once
 * with Undo in the snackbar: `remove` unlinks one (never deletes it); `reorder`
 * saves a new order for the unarchived ones (archived ones keep their order,
 * after them). The sortable list passes its own before / after order, so two
 * quick drags save and undo against what's on screen, not a stale refetch.
 */
export const useSubworkstreamEdits = (
  parent: Workstream
): SubworkstreamRemoval & {
  reorder: (unarchivedIds: number[], previousUnarchivedIds: number[]) => void;
} => {
  const {editWorkstream} = useWorkstreamEdit();
  // Saves `ids` as the sub-workstreams, then refreshes the rail's outline too.
  const save = (ids: number[]) =>
    editWorkstream({data: buildSubworkstreamsEdit(parent, ids)}).then(() => {
      void invalidateNavTrees();
    });
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const fail = (error: unknown) =>
    setSnackbarMessage({
      text: error instanceof Error && error.message ? error.message : 'Something went wrong',
    });

  const remove = (id: number, name: string) => {
    const ids = getSubworkstreamIds(parent);
    save(ids.filter((other) => other !== id))
      .then(() => {
        logWorkstreamsInteraction('subworkstream_removed', {
          workstream_id: parent.id,
          subworkstream_id: id,
        });
        setSnackbarMessage({
          text: `Removed "${name}" from ${parent.name}`,
          action: {
            text: 'Undo',
            onClick: () => {
              logWorkstreamsInteraction('subworkstream_remove_undone', {
                workstream_id: parent.id,
                subworkstream_id: id,
              });
              save(ids).catch(fail);
            },
          },
        });
      })
      .catch(fail);
  };

  const reorder = (unarchivedIds: number[], previousUnarchivedIds: number[]) => {
    const sortable = new Set(unarchivedIds);
    const archivedIds = getSubworkstreamIds(parent).filter((id) => !sortable.has(id));
    save([...unarchivedIds, ...archivedIds])
      .then(() => {
        logWorkstreamsInteraction('subworkstreams_reordered', {
          workstream_id: parent.id,
          subworkstream_count: unarchivedIds.length,
        });
        setSnackbarMessage({
          text: `Reordered sub-workstreams of ${parent.name}`,
          action: {
            text: 'Undo',
            onClick: () => {
              logWorkstreamsInteraction('subworkstreams_reordered', {
                workstream_id: parent.id,
                subworkstream_count: unarchivedIds.length,
                undo: true,
              });
              save([...previousUnarchivedIds, ...archivedIds]).catch(fail);
            },
          },
        });
      })
      .catch(fail);
  };

  return {parentName: parent.name, remove, reorder};
};

// The hover button on a detail page's sub-workstream card.
export const RemoveSubworkstreamButton = ({id, name}: {id: number; name: string}) => {
  const removal = useContext(SubworkstreamRemovalContext);
  if (!removal) return null;
  const label = `Remove from ${removal.parentName}`;
  return (
    <Tooltip title={label} placement="top">
      <IconButton
        variant="transparent"
        size="small"
        aria-label={label}
        onClick={(event: React.MouseEvent) => {
          // The card's header toggles it; this shouldn't.
          event.stopPropagation();
          removal.remove(id, name);
        }}
        // Nor should Enter / Space on it reach the header's keyboard toggle.
        onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
      >
        <UIIcon src={UnlinkLine} size="small" className={atoms({color: 'Text Subtle'})} />
      </IconButton>
    </Tooltip>
  );
};
